import {
    AlertThresholdRequest,
    AlertThresholdRequestMetricTypeConfig,
    IProfileChild,
    MetricTypeCodes,
} from '@kcftech/prometheus-client';
import {
    convertTemperatureFromBaseUnit,
    convertTemperatureToBaseUnit,
    getTemperatureDisplaySymbol,
} from 'utils/units';
import { round } from 'utils/number';

export type MetricTypeConfig = {
    metricType: MetricTypeCodes;
    selected: boolean;
    minAlarmBound?: number;
    maxAlarmBound?: number;
};

export type AlarmThresholdFormFields = Readonly<{
    start?: Date | null;
    end?: Date | null;
    warning?: number;
    criticalAlarm?: number;
    percentage?: number;
    points?: number;
    onlyToNotAlreadySetIndicators: boolean;
    groupIds: string[];
    indicatorTypes: MetricTypeConfig[];
}>;

export const INDICATOR_CHART_ITEMS = [
    {
        metricType: MetricTypeCodes.VibrationOverall,
        name: 'Overall Vibration',
        units: 'in/sec',
        defaultMin: 0.3,
        defaultMax: 1.5,
    },
    {
        metricType: MetricTypeCodes.VibrationOverallPeakAccel,
        name: 'Peak Acceleration',
        units: 'g',
        defaultMin: 6,
        defaultMax: 16,
    },
    {
        metricType: MetricTypeCodes.VibrationOverallPeak,
        name: 'Peak Velocity',
        units: 'in/sec',
        defaultMin: 0.3,
        defaultMax: 2,
    },
    {
        metricType: MetricTypeCodes.VibrationOverallRmsAccel,
        name: 'RMS Acceleration',
        units: 'g',
        defaultMin: 5,
        defaultMax: 14,
    },
    {
        metricType: MetricTypeCodes.VibrationOverallRms,
        name: 'RMS Velocity',
        units: 'in/sec',
        defaultMin: 0.3,
        defaultMax: 2,
    },
    {
        metricType: MetricTypeCodes.Temperature,
        name: 'Temperature',
        units: '°C',
        defaultMin: 54.44,
        defaultMax: 75,
    },
] as const;

function toNumberOrUndefined(value: number | string | undefined) {
    if (value === undefined || value === '') {
        return undefined;
    }

    return Number(value);
}

// Temperature is the only indicator here whose display unit varies with the user's profile.
export function getIndicatorChartItemUnits(
    item: (typeof INDICATOR_CHART_ITEMS)[number],
    profile: IProfileChild | undefined,
): string {
    return item.metricType === MetricTypeCodes.Temperature
        ? getTemperatureDisplaySymbol(profile)
        : item.units;
}

// Temperature's defaults are authored in Celsius (the base unit); convert
// them to the user's preferred unit before prefilling the form.
export function getDefaultIndicatorTypeConfigs(
    profile: IProfileChild | undefined,
): MetricTypeConfig[] {
    return INDICATOR_CHART_ITEMS.map((item) => {
        const toDisplayUnit = (bound: number) =>
            item.metricType === MetricTypeCodes.Temperature
                ? round(convertTemperatureFromBaseUnit(bound, profile), 1)
                : bound;

        return {
            metricType: item.metricType,
            selected: false,
            minAlarmBound: toDisplayUnit(item.defaultMin),
            maxAlarmBound: toDisplayUnit(item.defaultMax),
        };
    });
}

// The RunAlertThresholdJob API expects Temperature bounds in base units
// (Celsius), so convert them here before sending.
export function buildAlertThresholdRequest(
    data: AlarmThresholdFormFields,
    locationId: string,
    profile: IProfileChild | undefined,
): AlertThresholdRequest {
    const selectedIndicatorTypes = data.indicatorTypes
        .filter(({ selected }) => selected)
        .map(({ metricType, minAlarmBound, maxAlarmBound }) => {
            const toBaseUnit = (bound: number | undefined) =>
                bound !== undefined && metricType === MetricTypeCodes.Temperature
                    ? convertTemperatureToBaseUnit(bound, profile)
                    : bound;

            return new AlertThresholdRequestMetricTypeConfig({
                MetricType: metricType,
                MinAlarmBound: toBaseUnit(toNumberOrUndefined(minAlarmBound)),
                MaxAlarmBound: toBaseUnit(toNumberOrUndefined(maxAlarmBound)),
            });
        });

    return new AlertThresholdRequest({
        LocationId: locationId,
        OverwriteExistingThresholds: !data.onlyToNotAlreadySetIndicators,
        GroupIds: data.groupIds,
        IndicatorTypes: selectedIndicatorTypes,
        StartDate: data.start ?? undefined,
        EndDate: data.end ?? undefined,
        WarningPercentage: toNumberOrUndefined(data.warning),
        CriticalPercentage: toNumberOrUndefined(data.criticalAlarm),
        NotificationCriteriaPercentage: Number(data.percentage),
        NotificationCriteriaPoints: Number(data.points),
    });
}
